/* sand-game/index.js — DOM orchestrator for the Falling Sand sandbox.
 *
 * The only DOM-coupled module. The simulation runs on a fixed timestep
 * decoupled from rAF, so the physics speed is stable regardless of frame
 * rate; the rAF loop only blits and is paused when the tab is hidden.
 */

import {
    EMPTY, SAND, WATER, WALL, STONE, WOOD, FIRE, OIL, LAVA, ACID,
    PLANT, ICE, SALT, SPOUT, PAINTABLE,
} from './elements.js';
import { step } from './automaton.js';
import { createGrid, stampLine, filledCount, gridToPixels, pixelsToGrid } from './grid.js';
import { buildColorTable, blit, PALETTE_NAMES } from './sand-render.js';
import { dpr } from '../toy-fx/prefs.js';
import { initFunnel } from '../toy-fx/telemetry.js';

const SLUG = 'sand-game';
const SIM_HZ = 60;
const SIM_DT = 1000 / SIM_HZ;
const TARGET_CELL = 3;          // CSS px per grid cell at full resolution (finer grains; adaptive coarsen guard at mount() still protects weak devices)

const EMOJI = {
    0: '🩹', 2: '🟨', 3: '💧', 1: '🧱', 4: '🪨', 5: '🪵', 6: '🔥',
    7: '🛢️', 8: '🌋', 9: '🧪', 10: '🌱', 11: '🧊', 14: '🧂', 15: '🚿',
};

const I18N = (typeof window !== 'undefined' && window.SandGameI18n) || {};
const t = (k, f) => (I18N && I18N[k]) || f || k;
const $ = (id) => document.getElementById(id);
function ae(ev, p) { try { if (window.__ae) window.__ae(ev, Object.assign({ slug: SLUG }, p || {})); } catch (_) {} }

export function mount() {
    const stage = $('sg-stage');
    const canvas = $('sg-canvas');
    if (!stage || !canvas) return;
    const ctx = canvas.getContext('2d');
    const scratch = document.createElement('canvas');

    const state = {
        grid: null, gw: 0, gh: 0,
        cell: TARGET_CELL,
        element: SAND,
        brush: 6,
        shape: 'circle',
        palette: 'classic',
        paused: false,
        speed: 1,
        frame: 0,
        colorTable: buildColorTable('classic'),
        startedPainting: false,
    };
    let rafId = 0, lastTick = 0, acc = 0;
    let fpsSamples = [], fpsLast = 0, lowFpsStreak = 0;
    let cssW = 0, cssH = 0;

    /* ---- settings ---- */
    function restore() {
        try {
            const ts = window.__toolSettings; if (!ts) return;
            const el = parseInt(ts.load(SLUG, 'element', String(SAND)), 10);
            if (!isNaN(el)) state.element = el;
            state.brush = parseInt(ts.load(SLUG, 'brush', '6'), 10) || 6;
            state.shape = ts.load(SLUG, 'shape', 'circle');
            state.palette = ts.load(SLUG, 'palette', 'classic');
            state.speed = parseInt(ts.load(SLUG, 'speed', '1'), 10) || 1;
        } catch (_) {}
        state.colorTable = buildColorTable(state.palette);
    }
    function save(k, v) { try { window.__toolSettings && window.__toolSettings.save(SLUG, k, v); } catch (_) {} }

    /* ---- grid sizing ---- */
    function rebuildGrid(keepContent) {
        const rect = stage.getBoundingClientRect();
        cssW = Math.max(160, rect.width);
        cssH = Math.max(160, rect.height);
        const newGw = Math.max(40, Math.floor(cssW / state.cell));
        const newGh = Math.max(40, Math.floor(cssH / state.cell));
        const old = state.grid, ogw = state.gw, ogh = state.gh;
        state.gw = newGw; state.gh = newGh;
        state.grid = createGrid(newGw, newGh);
        if (keepContent && old) {
            // Nearest-neighbour copy of the old content.
            for (let y = 0; y < newGh; y++) {
                for (let x = 0; x < newGw; x++) {
                    const sx = Math.floor(x / newGw * ogw);
                    const sy = Math.floor(y / newGh * ogh);
                    state.grid[y * newGw + x] = old[sy * ogw + sx];
                }
            }
        }
        const r = dpr();
        canvas.width = Math.round(cssW * r);
        canvas.height = Math.round(cssH * r);
        canvas.style.width = cssW + 'px';
        canvas.style.height = cssH + 'px';
        ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    /* ---- rendering ---- */
    function render() {
        try { blit(ctx, state.grid, state.gw, state.gh, state.colorTable, scratch, canvas.width, canvas.height); }
        catch (e) { if (window.__handleError) window.__handleError(e, 'sand-game.render'); }
    }

    /* ---- simulation loop ---- */
    function loop(now) {
        rafId = 0;
        try {
            const dt = Math.min(120, now - lastTick);
            lastTick = now;
            if (!state.paused) {
                acc += dt;
                let ticks = 0;
                while (acc >= SIM_DT && ticks < 8) {
                    for (let s = 0; s < state.speed; s++) {
                        step(state.grid, state.gw, state.gh, Math.random, state.frame++);
                    }
                    acc -= SIM_DT;
                    ticks++;
                }
            }
            render();
            measureFps(now);
        } catch (e) {
            if (window.__handleError) window.__handleError(e, 'sand-game.loop');
        }
        rafId = requestAnimationFrame(loop);
    }
    function startLoop() {
        if (rafId) return;
        lastTick = performance.now();
        rafId = requestAnimationFrame(loop);
    }
    function stopLoop() {
        if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
    }

    function measureFps(now) {
        if (fpsLast) {
            const fps = 1000 / Math.max(1, now - fpsLast);
            fpsSamples.push(fps);
            if (fpsSamples.length >= 30) {
                const avg = fpsSamples.reduce((a, b) => a + b, 0) / fpsSamples.length;
                fpsSamples = [];
                const el = $('sg-fps');
                if (el) el.textContent = t('fps', 'FPS: {n}').replace('{n}', Math.round(avg));
                // Adaptive resolution — coarsen the grid if we are struggling.
                if (avg < 42 && state.cell < 12) {
                    lowFpsStreak++;
                    if (lowFpsStreak >= 3) {
                        lowFpsStreak = 0;
                        state.cell += 2;
                        rebuildGrid(true);
                        if (window.__toast) window.__toast(t('perf_note', 'Lowered resolution to keep it smooth'), 'info');
                    }
                } else { lowFpsStreak = 0; }
            }
        }
        fpsLast = now;
    }

    /* ---- painting ---- */
    let painting = false, lastPx = 0, lastPy = 0;
    function toGrid(clientX, clientY) {
        const rect = canvas.getBoundingClientRect();
        return {
            x: Math.floor((clientX - rect.left) / rect.width * state.gw),
            y: Math.floor((clientY - rect.top) / rect.height * state.gh),
        };
    }
    function paint(x, y, fromX, fromY) {
        stampLine(state.grid, state.gw, state.gh, fromX, fromY, x, y,
            state.brush, state.element, state.shape);
        if (!state.startedPainting) {
            state.startedPainting = true;
            ae('feature_use', { f: 'paint_start', element: state.element });
            try { window.__aeToolComplete && window.__aeToolComplete(); } catch (_) {}
        }
    }

    /* ---- snapshot ---- */
    function saveSnapshot() {
        try {
            const c = document.createElement('canvas');
            c.width = state.gw; c.height = state.gh;
            const cx = c.getContext('2d');
            const img = cx.createImageData(state.gw, state.gh);
            img.data.set(gridToPixels(state.grid, state.gw, state.gh));
            cx.putImageData(img, 0, 0);
            c.toBlob((blob) => {
                if (blob && window.__downloadFile) window.__downloadFile(blob, 'sand-game.png');
            }, 'image/png');
            ae('feature_use', { f: 'snapshot_save' });
        } catch (e) { if (window.__handleError) window.__handleError(e, 'sand-game.save'); }
    }
    function loadSnapshot(file) {
        if (!file) return;
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            try {
                const c = document.createElement('canvas');
                c.width = state.gw; c.height = state.gh;
                const cx = c.getContext('2d');
                cx.imageSmoothingEnabled = false;
                cx.drawImage(img, 0, 0, state.gw, state.gh);
                const data = cx.getImageData(0, 0, state.gw, state.gh).data;
                state.grid = pixelsToGrid(data, state.gw, state.gh);
                render();
                ae('feature_use', { f: 'snapshot_load' });
            } catch (e) { if (window.__handleError) window.__handleError(e, 'sand-game.load'); }
            URL.revokeObjectURL(url);
        };
        img.onerror = () => { URL.revokeObjectURL(url); };
        img.src = url;
    }

    /* ---- element picker ---- */
    function buildPicker() {
        const host = $('sg-elements');
        if (!host) return;
        host.innerHTML = '';
        const list = [EMPTY].concat(PAINTABLE);   // EMPTY = eraser
        list.forEach((id) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'sg-el';
            btn.dataset.el = String(id);
            btn.setAttribute('aria-pressed', id === state.element ? 'true' : 'false');
            const key = id === EMPTY ? 'el_eraser' : 'el_' + elementKey(id);
            btn.innerHTML = '<span class="sg-el-icon">' + (EMOJI[id] || '⬛') + '</span>'
                + '<span class="sg-el-name">' + t(key, key) + '</span>';
            btn.addEventListener('click', () => selectElement(id));
            host.appendChild(btn);
        });
    }
    function selectElement(id) {
        state.element = id;
        save('element', String(id));
        const host = $('sg-elements');
        if (host) {
            host.querySelectorAll('.sg-el').forEach((b) => {
                b.setAttribute('aria-pressed', parseInt(b.dataset.el, 10) === id ? 'true' : 'false');
            });
        }
        ae('feature_use', { f: 'element_select', element: id });
    }
    function elementKey(id) {
        const map = { 1: 'wall', 2: 'sand', 3: 'water', 4: 'stone', 5: 'wood', 6: 'fire',
            7: 'oil', 8: 'lava', 9: 'acid', 10: 'plant', 11: 'ice', 14: 'salt', 15: 'spout' };
        return map[id] || 'sand';
    }

    /* ---- controls ---- */
    function setPaused(p) {
        state.paused = p;
        const btn = $('sg-pause');
        if (btn) btn.textContent = p ? t('play', 'Play') : t('pause', 'Pause');
        ae('feature_use', { f: 'pause_toggle', paused: p });
    }
    function clearGrid() {
        state.grid = createGrid(state.gw, state.gh);
        render();
        ae('feature_use', { f: 'clear' });
        if (window.__announce) window.__announce(t('cleared', 'Canvas cleared'));
    }

    function wire() {
        canvas.addEventListener('pointerdown', (e) => {
            painting = true;
            const g = toGrid(e.clientX, e.clientY);
            lastPx = g.x; lastPy = g.y;
            paint(g.x, g.y, g.x, g.y);
            canvas.setPointerCapture(e.pointerId);
        });
        canvas.addEventListener('pointermove', (e) => {
            if (!painting) return;
            const g = toGrid(e.clientX, e.clientY);
            paint(g.x, g.y, lastPx, lastPy);
            lastPx = g.x; lastPy = g.y;
        });
        const stop = () => { painting = false; };
        canvas.addEventListener('pointerup', stop);
        canvas.addEventListener('pointercancel', stop);

        bindRange('sg-brush', (v) => { state.brush = v; save('brush', String(v)); });
        bindRange('sg-speed', (v) => { state.speed = Math.max(1, v); save('speed', String(v)); });
        bindSelect('sg-shape', 'shape', (v) => { save('shape', v); });
        bindSelect('sg-palette', 'palette', (v) => {
            state.colorTable = buildColorTable(v); save('palette', v); render();
            ae('feature_use', { f: 'palette_change' });
        });

        bindClick('sg-pause', () => setPaused(!state.paused));
        bindClick('sg-step', () => {
            if (!state.paused) setPaused(true);
            step(state.grid, state.gw, state.gh, Math.random, state.frame++);
            render();
        });
        bindClick('sg-clear', clearGrid);
        bindClick('sg-save', saveSnapshot);
        const loadInput = $('sg-load');
        if (loadInput) loadInput.addEventListener('change', (e) => loadSnapshot(e.target.files[0]));

        // Element hotkeys 1-9, 0.
        document.addEventListener('keydown', (e) => {
            if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
            const list = [EMPTY].concat(PAINTABLE);
            if (/^[0-9]$/.test(e.key)) {
                const idx = e.key === '0' ? 10 : parseInt(e.key, 10);
                if (list[idx] != null) selectElement(list[idx]);
            }
        });

        window.addEventListener('resize', debounce(() => { rebuildGrid(true); render(); }, 180));
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) stopLoop();
            else startLoop();
        });
        window.addEventListener('beforeunload', stopLoop);
    }
    function bindClick(id, fn) { const el = $(id); if (el) el.addEventListener('click', fn); }
    function bindRange(id, fn) {
        const el = $(id); if (!el) return;
        if (id === 'sg-brush') el.value = state.brush;
        if (id === 'sg-speed') el.value = state.speed;
        el.addEventListener('input', () => fn(parseInt(el.value, 10) || 1));
    }
    function bindSelect(id, key, fn) {
        const el = $(id); if (!el) return;
        el.value = state[key];
        el.addEventListener('change', () => { state[key] = el.value; fn(el.value); });
    }

    /* ---- boot ---- */
    restore();
    rebuildGrid(false);
    buildPicker();
    wire();
    render();
    startLoop();
    try { initFunnel(SLUG); } catch (_) {}

    window.__sgDebug = {
        state: state,
        paint: (x, y) => paint(x, y, x, y),
        tick: (n) => { for (let i = 0; i < (n || 1); i++) step(state.grid, state.gw, state.gh, Math.random, state.frame++); render(); },
        filled: () => filledCount(state.grid),
        clear: clearGrid,
        select: selectElement,
    };
}

function debounce(fn, ms) {
    let timer = 0;
    return function () { clearTimeout(timer); timer = setTimeout(fn, ms); };
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
    else mount();
}
